import { put, takeEvery } from 'redux-saga/effects'
import * as Eff from 'redux-saga/effects' // tslint:disable-line:no-duplicate-imports
import axios, { AxiosError } from 'axios'
import Analytics from '@Utils/Analytics'
import toastr from 'toastr'

import Logger from '@Utils/Logger'
import { IAction, ISuccessResponse } from '@Root/types'
import router from '@Site/Router'

import {
  signUpSuccess,
  signUpFailure,
  signInSuccess,
  signInFailure,
  passwordNewSuccess,
  passwordNewFailure,
  updatePasswordSuccess,
  updatePasswordFailure,
  createOTPSuccess,
  createOTPFailure,
  verifyOTPSuccess,
  verifyOTPFailure,
} from './actions'

import { getUserInfo } from '@SiteModules/User/actions'

import {
  AuthenticationActionTypes,
  ISignUpPayload,
  ISignInPayload,
  ISsoSignInPayload,
  IPasswordNewPayload,
  IUpdatePasswordPayload,
  ICreateOTPPayload,
  IVerifyOTPPayload,
} from './types'

import * as API from './api'

const call: any = Eff.call

const TIMEOUT = 3000 // ms
const DISPOSABLE_EMAIL_CHECK_TIMEOUT = 3000 // ms

export const isDisposableEmailDomain = async (domain: string): Promise<boolean> => {
  try {
    const response = await axios.get(
      `https://open.kickbox.com/v1/disposable/${domain}`,
      { timeout: DISPOSABLE_EMAIL_CHECK_TIMEOUT }
    )

    return response.data.disposable === true
  } catch (_error) {
    // Account creation must remain available when the optional third-party
    // domain check is unavailable, blocked by CORS, or times out.
    return false
  }
}

function* signUpSaga(action: IAction<ISignUpPayload>): Generator<any, any, any> {
  try {
    const domain = action.payload.email.split('@')[1]
    const isDisposableEmail = yield call(isDisposableEmailDomain, domain)

    if (isDisposableEmail) {
      toastr.error('Email domain is not permitted', 'Error')
      setTimeout(() => router.navigate('/users/sign_up', { replace: true }), TIMEOUT)
      return
    }

    const urlParams = new URLSearchParams(window.location.search)
    const anonymousId = urlParams.get('anonymous_id')
    const payload = {
      ...action.payload,
      ...(anonymousId ? { anonymousId } : {}),
    }
    yield call(API.signUp, payload)
    yield put(signUpSuccess())

    window.Checksub.token = window.Checksub.cookies.get('token')
    yield put(getUserInfo())
    yield call(router.navigate, '/onboarding', { replace: true })
  } catch (e) {
    Logger.error(e)
    yield put(signUpFailure())
    setTimeout(() => router.navigate('/users/sign_up', { replace: true }), TIMEOUT)
  }
}

function* signInSaga(action: IAction<ISignInPayload>): Generator<any, any, any> {
  try {
    const response = 'useSso' in action.payload ?
      yield call(API.ssoSignIn, action.payload) :
      yield call(API.signIn, action.payload)
    const { data } = response.data
    yield put(signInSuccess(data))

    Analytics.trackGTMSignUpLogin('login', 'email')

    yield put(getUserInfo())

    window.Checksub.token = window.Checksub.cookies.get('token')
    yield call(router.navigate, '/', { replace: true })
  } catch (e) {
    Logger.error(e, 'Log-in failed')
    yield put(signInFailure())
  }
}

interface IRedirectUri {
  readonly redirect_uri: string
}

function* ssoSignInSaga(action: IAction<ISsoSignInPayload>): Generator<any, any, any> {
  try {
    const response: ISuccessResponse<IRedirectUri> = yield call(API.ssoSignIn, action.payload)
    window.location.href = response.data.redirect_uri
  } catch (e) {
    Logger.error(e, 'Log-in failed')
    yield put(signInFailure())
  }
}

function* passwordNewSaga(action: IAction<IPasswordNewPayload>) {
  try {
    yield call(API.passwordNew, action.payload)
    yield put(passwordNewSuccess())
    yield call(router.navigate, '/', { replace: true })
    toastr.success('', 'Link to reset password sent')
  } catch (e) {
    Logger.error(e, 'Link to reset password could not be sent')
    yield put(passwordNewFailure())
  }
}

function* updatePasswordSaga(action: IAction<IUpdatePasswordPayload>) {
  try {
    yield call(API.updatePassword, action.payload)
    yield put(updatePasswordSuccess())
    yield call(router.navigate, '/', { replace: true })
    toastr.success('', 'Password changed')
  } catch (err) {
    const e = err as AxiosError<{ message?: string; code?: string }>
    yield put(updatePasswordFailure())

    const message = e.response?.data?.message
    const code = e.response?.data?.code

    if (code === 'token_expired') {
      toastr.error(message || 'Your reset token has expired')
      yield call(router.navigate, '/users/password/new', { replace: true })
    } else {
      Logger.error(e, 'Password could not be changed')
      toastr.error(message || 'Password could not be changed')
    }
  }
}

function* createOTPSaga(action: IAction<ICreateOTPPayload>) {
  try {
    yield call(API.createOTP)
    yield put(createOTPSuccess())
    if (action.payload.shouldNotify) {
      toastr.success('', 'OTP resent')
    }
  } catch (e) {
    Logger.error(e, 'OTP could not be sent')
    yield put(createOTPFailure())
  }
}

function* verifyOTPSaga(action: IAction<IVerifyOTPPayload>) {
  try {
    yield call(API.verifyOTP, action.payload)
    yield put(verifyOTPSuccess())

    yield call(router.navigate, '/onboarding/tips_preview', { replace: true })
  } catch (e) {
    Logger.error(e, 'OTP verification failed')
    yield put(verifyOTPFailure())
  }
}

function* authenticationSagas() {
  yield takeEvery(AuthenticationActionTypes.SIGN_UP, signUpSaga)
  yield takeEvery(AuthenticationActionTypes.SIGN_IN, signInSaga)
  yield takeEvery(AuthenticationActionTypes.SSO_SIGN_IN, ssoSignInSaga)
  yield takeEvery(AuthenticationActionTypes.PASSWORD_NEW, passwordNewSaga)
  yield takeEvery(AuthenticationActionTypes.UPDATE_PASSWORD, updatePasswordSaga)
  yield takeEvery(AuthenticationActionTypes.CREATE_OTP, createOTPSaga)
  yield takeEvery(AuthenticationActionTypes.VERIFY_OTP, verifyOTPSaga)
}

export default authenticationSagas
